
import { defineComponent, PropType, computed } from 'vue';
import {
  ButtonSizeEnums,
  ButtonsType,
  ButtonTypeColors,
  ButtonTypes,
} from '@/core/enums/buttons.enums';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { getButtonIcon } from '@/core/config/buttonIcons.config';

export default defineComponent({
  name: 'ButtonCustom',
  inheritAttrs: false,

  components: {
    FontAwesomeIcon,
  },

  emits: ['click'],

  props: {
    buttonType: {
      type: String as PropType<ButtonsType>,
      default: ButtonsType.SAVE,
    },

    type: {
      type: String as PropType<ButtonTypes>,
      default: ButtonTypes.BUTTON,
    },

    colorType: {
      type: String as PropType<ButtonTypeColors>,
      default: ButtonTypeColors.PRIMARY,
    },

    classes: {
      type: String,
      default: '',
    },

    isDisabled: {
      type: Boolean,
      default: false,
    },

    isLoading: {
      type: Boolean,
      default: false,
    },

    immediate: {
      type: Boolean,
      default: true,
    },

    delayTime: {
      type: Number,
      default: 3000,
    },

    showIcon: {
      type: Boolean,
      default: true,
    },

    size: {
      type: String as PropType<ButtonSizeEnums>,
      default: ButtonSizeEnums.LARGE,
    },

    tooltipMessage: {
      type: String,
      default: '',
    },

    iconSize: {
      type: String,
      default: 'h-5 w-5',
    },

    // Additional customization props
    variant: {
      type: String as PropType<'default' | 'outline' | 'ghost'>,
      default: 'default',
    },

    rounded: {
      type: Boolean,
      default: false,
    },

    fullWidth: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    const iconComputed = computed(() => {
      return getButtonIcon(props.buttonType);
    });

    const iconClassComputed = computed(() => {
      return `${props.iconSize} fill-current inline-block`;
    });

    // Wrapper class để cursor hiển thị đúng khi disabled
    const wrapperClass = computed(() => {
      return props.isDisabled ? 'cursor-not-allowed inline-block' : 'inline-block';
    });

    return {
      iconComputed,
      iconClassComputed,
      wrapperClass,
    };
  },

  data() {
    return {
      disabled: false,
      timeOut: 0,
      ButtonsType,
    };
  },

  computed: {
    classComputed() {
      const baseClasses = [
        'btn-customize',
        `btn-focus-${this.colorType}`,
        'm-0',
        'btn',
        `btn-${this.colorType}`,
        'align-self-center',
        'mr-1',
        'mb-1',
        'mt-1',
        this.classes,
        this.size,
      ];

      // Handle rounded corners
      if (this.rounded) {
        baseClasses.push('rounded');
      } else {
        baseClasses.push('rounded-none');
      }

      // Handle full width
      if (this.fullWidth) {
        baseClasses.push('w-100');
      }

      // Handle variants
      if (this.variant === 'outline') {
        baseClasses.push('btn-outline');
      } else if (this.variant === 'ghost') {
        baseClasses.push('btn-ghost');
      }

      return baseClasses.filter(Boolean).join(' ').trim();
    },

    disabledStyle() {
      return this.tooltipMessage && (this.isDisabled || this.disabled)
        ? 'pointer-events: none'
        : '';
    },

    hasLabelSlot() {
      return !!this.$slots['button-label'];
    },
  },

  methods: {
    click() {
      this.$emit('click');
    },

    onButtonClicked() {
      this.click();

      if (this.immediate) {
        return;
      }

      this.disabled = true;
      this.timeOut = setTimeout(() => {
        this.disabled = false;
      }, this.delayTime);
    },

    // Utility method to check if button is in a specific state
    isButtonState(state: 'loading' | 'disabled' | 'active') {
      switch (state) {
        case 'loading':
          return this.isLoading;
        case 'disabled':
          return this.isDisabled || this.disabled;
        case 'active':
          return !this.isLoading && !this.isDisabled && !this.disabled;
        default:
          return false;
      }
    },
  },

  beforeUnmount() {
    clearTimeout(this.timeOut);
  },
});
